import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { MessageCircle } from 'lucide-react';
import { LeadFormSection } from '../components/landing';
import { WHATSAPP_MANKLAR_URL } from '../lib/contacto';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Página pública de /ref/:code, el enlace que comparte cada cliente
// (pages/Invite.js). Con la landing aparcada es la única entrada de leads
// desde la web: sólo muestra el formulario si el código existe.
export default function ReferralLanding() {
  const { code } = useParams();
  const codigo = (code || '').toUpperCase();
  const [estado, setEstado] = useState('cargando'); // cargando | valido | invalido | error
  const [referrerName, setReferrerName] = useState('');

  useEffect(() => {
    // StrictMode ejecuta el efecto dos veces en desarrollo: se ignora la
    // respuesta de la petición que ya no vale.
    let cancelado = false;
    setEstado('cargando');

    axios.get(`${API_URL}/api/referral/validate/${encodeURIComponent(codigo)}`)
      .then(({ data }) => {
        if (cancelado) return;
        if (data.valid) {
          setReferrerName(data.referrer_name);
          setEstado('valido');
        } else {
          setEstado('invalido');
        }
      })
      .catch((err) => {
        if (cancelado) return;
        setEstado(err.response?.status === 404 ? 'invalido' : 'error');
      });

    return () => {
      cancelado = true;
    };
  }, [codigo]);

  if (estado === 'cargando') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#030303' }}>
        <div className="flex flex-col items-center gap-4">
          <img src="/manklar-logo-white.png" alt="Manklar" className="h-12 opacity-50" />
          <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          <p className="text-zinc-500 text-sm font-['Manrope']">Comprobando tu invitación...</p>
        </div>
      </div>
    );
  }

  const whatsappUrl = `${WHATSAPP_MANKLAR_URL}?text=${encodeURIComponent(
    `Hola, me han invitado a Manklar con el código ${codigo} y quería solicitar la prueba gratuita.`
  )}`;

  return (
    <div className="min-h-screen bg-[#030303] overflow-x-hidden">
      <header className="pt-10 sm:pt-14 flex justify-center">
        <img src="/manklar-logo-white.png" alt="Manklar" className="h-10 sm:h-12" />
      </header>

      {estado === 'valido' && (
        <LeadFormSection referralCode={codigo} referrerName={referrerName} />
      )}

      {estado === 'invalido' && (
        <AvisoInvitacion
          titulo="Enlace de invitación no válido"
          texto="Este enlace de invitación no es válido. Pide a quien te invitó que te lo reenvíe, o escríbenos por WhatsApp."
          whatsappUrl={whatsappUrl}
        />
      )}

      {estado === 'error' && (
        <AvisoInvitacion
          titulo="No hemos podido comprobar tu invitación"
          texto="Inténtalo de nuevo en unos minutos, o escríbenos por WhatsApp."
          whatsappUrl={whatsappUrl}
        />
      )}
    </div>
  );
}

function AvisoInvitacion({ titulo, texto, whatsappUrl }) {
  return (
    <section className="py-16 sm:py-24 px-4 sm:px-6">
      <div className="max-w-md mx-auto text-center">
        <h1 className="text-2xl sm:text-3xl font-medium text-white font-['Outfit'] mb-4">
          {titulo}
        </h1>
        <p className="text-base text-zinc-400 font-['Manrope'] mb-8">
          {texto}
        </p>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-12 sm:h-14 px-6 sm:px-8 bg-[#25D366] text-black font-semibold rounded-full hover:bg-[#20bd5a] transition-all items-center justify-center gap-2 text-sm sm:text-base"
        >
          <MessageCircle className="w-4 sm:w-5 h-4 sm:h-5" />
          Escribir por WhatsApp
        </a>
      </div>
    </section>
  );
}
